import React, { useState, useEffect, useCallback, memo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { storage } from "../../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import axios from "axios";
import { useMemo } from "react";

const AddUsers = memo(() => {
  const { currentUser } = useSelector((state) => state.user);
  const [formData, setFormData] = useState({
    role: "employee",
    branch: []
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [branches, setBranches] = useState([]);
  const [minBetAmount, setMinBetAmount] = useState(10);
  const navigate = useNavigate();

  // Memoized fetch function
  const fetchBranches = useCallback(async () => {
  try {
    const response = await axios.get(
      `/api/branch/getbranch/${currentUser.username}`
    );
    setBranches(response.data.branches || []); // Ensure we're setting the branches array
  } catch (err) {
    console.error("Error fetching branches:", err);
    setError("Error fetching branches");
    setBranches([]); // Set to empty array on error
  }
}, [currentUser.username]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  // Form handlers
  const handleChange = useCallback((e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  }, []);

  const handleBranchChange = useCallback((branchName) => {
    if (formData.role === 'admin') {
      setFormData(prev => ({
        ...prev,
        branch: prev.branch.includes(branchName)
          ? prev.branch.filter(b => b !== branchName)
          : [...prev.branch, branchName]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        branch: branchName
      }));
    }
  }, [formData.role]);

  const handleFileChange = useCallback((e) => {
    setImageFile(e.target.files[0]);
  }, []);

  const handleRoleChange = useCallback((e) => {
    const role = e.target.value;
    setFormData(prev => ({
      ...prev,
      role,
      branch: role === 'admin' ? [] : ''
    }));
  }, []);

  // Form submission with error handling
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      // Upload image if exists
      let imageUrl = formData.imageUrl;
      if (imageFile) {
        const imageRef = ref(storage, `Images/${imageFile.name}`);
        await uploadBytes(imageRef, imageFile);
        imageUrl = await getDownloadURL(imageRef);
      }

      // Prepare data for API
      const dataToSend = {
        ...formData,
        imageUrl,
        branch: formData.role === 'admin' ? formData.branch.join(',') : formData.branch,
        minBetAmount
      };

      const response = await axios.post(
        `/api/user/signup/${currentUser._id}`,
        dataToSend
      );

      if (response.data.success) {
        alert("User created successfully!");
        navigate("/admin");
      } else {
        setError(response.data.message || "Failed to create user");
      }
    } catch (error) {
      console.error("Create user error:", error);
      setError(error.response?.data?.message || "Error creating user");
    } finally {
      setLoading(false);
    }
  }, [formData, imageFile, minBetAmount, currentUser._id, navigate]);

  // Branch options based on user role
  const branchOptions = useMemo(() => {
  // If currentUser has branches and is admin/employee
  if (["admin", "employee"].includes(currentUser.role)) {
    const userBranches = currentUser.branch ? currentUser.branch.split(',') : [];
    return userBranches.map((branch, index) => (
      <option key={index} value={branch}>
        {branch}
      </option>
    ));
  }
  
  // For agent or other cases, use the fetched branches
  return (branches || []).map(branch => (
    <option key={branch._id} value={branch.name}>
      {branch.name}
    </option>
  ));
}, [currentUser, branches]);

// Update the branch checkbox rendering
{branches && branches.length > 0 ? (
  <div className="tw-grid tw-grid-cols-2 tw-gap-2">
    {branches.map((branch) => (
      <div key={branch._id} className="tw-flex tw-items-center tw-gap-2">
        <input
          type="checkbox"
          id={`branch-${branch._id}`}
          checked={Array.isArray(formData.branch) && formData.branch.includes(branch.name)}
          onChange={() => handleBranchChange(branch.name)}
          className="tw-w-4 tw-h-4"
        />
        <label htmlFor={`branch-${branch._id}`}>{branch.name}</label>
      </div>
    ))}
  </div>
) : (
  <p>No branches available</p>
)}


  return (
    <div className="tw-flex tw-justify-center tw-bg-slate-100 tw-items-center tw-min-h-screen">
      <div className="tw-border-t-8 tw-border-t-blue-500 tw-bg-white tw-p-8 tw-rounded-2xl sm:tw-w-[650px] tw-border tw-border-slate-300 tw-m-8">
        <h1 className="tw-text-2xl tw-font-bold tw-text-center tw-mb-6">Create New User</h1>
        
        <form onSubmit={handleSubmit} className="tw-flex tw-flex-col tw-gap-4">
          {/* Form Fields */}
          {[
            { id: "name", type: "text", placeholder: "Full Name", required: true },
            { id: "username", type: "text", placeholder: "Username", required: true },
            { id: "phone", type: "number", placeholder: "Phone number", required: true },
            { id: "password", type: "password", placeholder: "Password", required: true },
            { id: "cut", type: "text", placeholder: "Cut", required: true }
          ].map((field) => (
            <input
              key={field.id}
              type={field.type}
              id={field.id}
              placeholder={field.placeholder}
              required={field.required}
              onChange={handleChange}
              className="tw-w-full tw-h-10 tw-rounded-lg tw-border tw-border-slate-300 tw-p-3 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
            />
          ))}

          {/* Image Upload */}
          <div className="tw-w-full">
            <label className="tw-block tw-text-gray-700 tw-mb-2" htmlFor="imageUrl">
              Profile Image
            </label>
            <input 
              type="file"
              id="imageUrl"
              onChange={handleFileChange}
              className="tw-w-full"
              accept="image/*"
            />
          </div>

          {/* Role Selection */}
          <div className="tw-flex tw-gap-5 tw-w-full">
            <label htmlFor="role" className="tw-text-lg tw-font-bold tw-min-w-[80px]">
              Role:
            </label>
            <select
              id="role"
              value={formData.role}
              onChange={handleRoleChange}
              className="tw-flex-grow tw-rounded-lg tw-border tw-border-slate-300 tw-p-2.5 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
            >
              <option value="employee">Employee</option>
              {currentUser.role === "agent" && (
                <option value="admin">Admin</option>
              )}
            </select>
          </div>

          {/* Branch Selection */}
          <div className="tw-flex tw-gap-5 tw-w-full">
            <label className="tw-text-lg tw-font-bold tw-min-w-[80px]">
              Branch:
            </label>
            <div className="tw-flex-grow">
              {formData.role === 'admin' && currentUser.role === 'agent' ? (
                <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                  {branches.map((branch) => (
                    <div key={branch._id} className="tw-flex tw-items-center tw-gap-2">
                      <input
                        type="checkbox"
                        id={`branch-${branch._id}`}
                        checked={formData.branch.includes(branch.name)}
                        onChange={() => handleBranchChange(branch.name)}
                        className="tw-w-4 tw-h-4"
                      />
                      <label htmlFor={`branch-${branch._id}`}>{branch.name}</label>
                    </div>
                  ))}
                </div>
              ) : (
                <select
                  id="branch"
                  onChange={(e) => handleBranchChange(e.target.value)}
                  value={formData.branch}
                  className="tw-w-full tw-rounded-lg tw-border tw-border-slate-300 tw-p-2.5 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                >
                  <option value="">Select Branch</option>
                  {branchOptions}
                </select>
              )}
            </div>
          </div>

          {/* Minimum Bet Amount */}
          <div className="tw-w-full">
            <label className="tw-block tw-text-gray-700 tw-mb-2" htmlFor="minBetAmount">
              Minimum Bet Amount: {minBetAmount}
            </label>
            <input
              type="range"
              id="minBetAmount"
              min="10"
              max="100"
              step="10"
              value={minBetAmount}
              onChange={(e) => setMinBetAmount(Number(e.target.value))}
              className="tw-w-full"
            />
          </div>

          {/* Submit Button */}
          <button
            disabled={loading}
            type="submit"
            className="tw-w-full tw-font-semibold tw-p-3 tw-rounded-lg tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 disabled:tw-opacity-50"
          >
            {loading ? "Creating User..." : "Create User"}
          </button>
        </form>

        {error && <p className="tw-text-red-500 tw-mt-4 tw-text-center">{error}</p>}
      </div>
    </div>
  );
});

export default AddUsers;